<template>
	<div class="CopilotFieldControls">
		<div v-for="item of placed" :key="item.group.key"
			class="CopilotFieldControls__group" :class="{ 'CopilotFieldControls__group--pending': item.group.pending }"
			role="group" :aria-label="summary(item.group)" :style="{ top: `${item.top}px`, left: `${item.left}px` }"
			data-cy="copilot-field-controls"
		>
			<v-tooltip activator="parent" location="top" max-width="380">
				<div v-for="(line, index) of lines(item.group)" :key="index">{{ line }}</div>
			</v-tooltip>
			<span v-if="item.group.entries.some(isAssumption)" class="CopilotFieldControls__assumption"
				role="img" :aria-label="$t('text.copilotAssumption')" data-cy="copilot-field-assumption"
			>
				<v-icon size="14">mdi-help-circle-outline</v-icon>
			</span>
			<v-btn icon="mdi-check" size="x-small" density="comfortable" variant="flat" color="success"
				:aria-label="$t('text.copilotAccept')" data-cy="copilot-field-accept" @click="$emit('accept', ids(item.group))"
			/>
			<v-btn icon="mdi-undo" size="x-small" density="comfortable" variant="flat"
				:aria-label="$t('text.copilotRevert')" data-cy="copilot-field-revert" @click="$emit('revert', ids(item.group))"
			/>
		</div>
		<span v-for="mark of placedMarks" :key="mark.key"
			class="CopilotFieldControls__mark" role="img" :aria-label="$t('text.copilotAiGenerated')"
			:style="{ top: `${mark.top}px`, left: `${mark.left}px` }" data-cy="copilot-ai-mark" :data-key="mark.key"
		>
			<v-tooltip activator="parent" location="top">{{ $t('text.copilotAiGenerated') }}</v-tooltip>
			<v-icon size="14">mdi-creation</v-icon>
		</span>
	</div>
</template>

<script lang="js">
// AAL-86 ticket 14: accept and revert on the form itself. One overlay layer for the whole page,
// rendered by ServiceDesigner.vue; the child sections are not touched. For every field element with
// an open entry (proposed or pending, see copilot/field-controls.ts) it shows a small check and undo
// pinned to the field's top edge. The buttons emit the entry ids; the page runs them through the same
// review state the drawer list shows, so both stay in sync. Positions are re-read on scroll (any
// scroll container), resize, size changes of the page and the fields, when the entries change and
// when the page calls refresh() after an apply.
// Ticket 18: the same layer shows a small AI icon at the fields the saved Service lists in
// `metadata.aiGenerated` (`marks`, resolved by the page), except where controls already sit.
// Ticket 19: a group with an assumption (confidence below ASSUMPTION_CONFIDENCE) shows a question
// mark, and its tooltip line says so.
import { controlGroups, placeControls } from '@/copilot/field-controls'
import { displayValue, fieldTitle, hasValue, isAssumption } from '@/copilot/copilot-display'

export default {
	name: 'CopilotFieldControls',
	props: {
		/** ReviewEntry[] of copilot/proposal-apply.ts */
		entries: { type: Array, default: () => [] },
		/** (entry) => Element | null: the field element of an entry (page adapter's copilotFieldElement) */
		locate: { type: Function, required: true },
		/** px covered by the fixed header and step bar; controls of fields under it are hidden */
		topInset: { type: Number, default: 0 },
		/** () => AiIconTarget[] (copilot/copilot-metadata.ts): fields saved as AI-generated; read on refresh() */
		marks: { type: Function, default: null },
	},
	emits: [ 'accept', 'revert' ],
	data: () => ({
		placed: [],
		placedMarks: [],
	}),
	watch: {
		entries: {
			deep: true,
			handler() { this.$nextTick(() => this.refresh()) },
		},
	},
	mounted() {
		this.frame = null
		this.pendingRefresh = false
		this.groups = []
		this.marked = []
		window.addEventListener('scroll', this.schedulePlace, { capture: true, passive: true })
		window.addEventListener('resize', this.schedulePlace, { passive: true })
		this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.schedule())
		this.resizeObserver?.observe(document.body)
		this.refresh()
	},
	beforeUnmount() {
		window.removeEventListener('scroll', this.schedulePlace, { capture: true })
		window.removeEventListener('resize', this.schedulePlace)
		this.resizeObserver?.disconnect()
		if (this.frame !== null) cancelAnimationFrame(this.frame)
	},
	methods: {
		// at most one re-read per frame; a size change may render or remove fields, so it looks them up again
		schedule() {
			this.scheduleFrame('refresh')
		},
		// scroll and resize only move the fields
		schedulePlace() {
			this.scheduleFrame('place')
		},
		scheduleFrame(kind) {
			if (kind === 'refresh') this.pendingRefresh = true
			if (this.frame !== null) return
			this.frame = requestAnimationFrame(() => {
				const refresh = this.pendingRefresh
				this.frame = null
				this.pendingRefresh = false
				if (refresh) this.refresh()
				else this.place()
			})
		},
		// looks up the field elements of the open entries and the AI marks, then places them
		refresh() {
			this.groups = controlGroups(this.entries, this.locate)
			const controlled = new Set(this.groups.map(group => group.element))
			const marked = []
			for (const target of this.marks?.() ?? []) {
				const element = this.locate(target)
				if (!element || controlled.has(element) || marked.some(m => m.element === element)) continue
				marked.push({ key: target.key, element })
			}
			this.marked = marked
			for (const { element } of [ ...this.groups, ...marked ]) this.resizeObserver?.observe(element)
			this.place()
		},
		place() {
			const viewport = { topInset: this.topInset, viewportHeight: window.innerHeight }
			const at = element => placeControls(element.getBoundingClientRect(), viewport)
			this.placed = this.groups.map(group => ({ group, ...at(group.element) })).filter(item => item.top !== undefined)
			this.placedMarks = this.marked.map(mark => ({ key: mark.key, ...at(mark.element) })).filter(item => item.top !== undefined)
		},
		isAssumption,
		ids(group) {
			return group.entries.map(entry => entry.id)
		},
		lines(group) {
			return group.entries.map(entry => isAssumption(entry) ? `${this.line(entry)} (${this.$t('text.copilotAssumption')})` : this.line(entry))
		},
		line(entry) {
			const title = fieldTitle(key => this.$t(key), entry)
			const value = displayValue(entry.op === 'remove' ? entry.previous : entry.value)
			if (entry.op === 'remove') return `${title}: ${this.$t('text.copilotOpRemove')} ${value}`
			if (entry.status === 'pending' && hasValue(entry.previous)) return `${title}: ${displayValue(entry.previous)} → ${value}`
			return `${title}: ${value}`
		},
		summary(group) {
			return `Copilot: ${this.lines(group).join('; ')}`
		},
	},
}
</script>

<style scoped lang="scss">
.CopilotFieldControls {
	position: fixed;
	inset: 0;
	pointer-events: none;
	/* above the page, below the navbar (900), the sidebars and the Copilot drawer (100) */
	z-index: 50;
}
.CopilotFieldControls__group {
	position: absolute;
	transform: translateX(-100%);
	display: flex;
	gap: 2px;
	padding: 1px;
	border-radius: 12px;
	background: white;
	box-shadow: 0 0 0 1px #7e57c2;
	pointer-events: auto;
	&--pending { box-shadow: 0 0 0 1px #ffb300; }
}
.CopilotFieldControls__assumption {
	display: inline-flex;
	align-items: center;
	padding: 0 2px;
	color: #8d6e00;
}
.CopilotFieldControls__mark {
	position: absolute;
	transform: translateX(-100%);
	display: inline-flex;
	padding: 1px 3px;
	border-radius: 8px;
	background: #ede7f6;
	color: #7e57c2;
	pointer-events: auto;
}
</style>
